import React, { useEffect, useState } from "react";
import { getMetaData, hotelBookingReport } from "../../../api/admin/hotel";
import * as XLSX from "xlsx";
import SendButton from "../../../components/admin/usable/SendButton";
import CancelButton from "../../../components/admin/usable/CancelButton";

const HotelBookingReport = () => {
  const [Data, setData] = useState([]);
  const [Hotels, setHotels] = useState([]);
  const [Customers, setCustomers] = useState([]);
  const [StartDate, setStartDate] = useState("");
  const [EndDate, setEndDate] = useState("");
  const [SelectedHotel, setSelectedHotel] = useState("");
  const [SelectedCustomer, setSelectedCustomer] = useState("");
  const [IsLoading, setIsLoading] = useState(false);

  useEffect(() => {
    hotelBookingReport(
      setData,
      StartDate,
      EndDate,
      SelectedHotel,
      SelectedCustomer,
      setIsLoading,
    );
  }, [StartDate, EndDate, SelectedHotel, SelectedCustomer]);

  useEffect(() => {
    getMetaData(setHotels, setCustomers, setIsLoading);
  }, []);

  const handleHotelChange = (e) => setSelectedHotel(e.target.value);
  const handleCustomerChange = (e) => setSelectedCustomer(e.target.value);

  const totalSummary = Data.reduce(
    (acc, item) => {
      acc.totalAmount += Number(item.totalAmount) || 0;
      acc.paidAmount += Number(item.paidAmount) || 0;
      acc.taxAmount += Number(item.taxAmount) || 0;
      return acc;
    },
    { totalAmount: 0, paidAmount: 0, taxAmount: 0 },
  );

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      Data.map((item, index) => ({
        ID: index + 1,
        Invoice: item.invoiceId,
        Transaction: item.transactionId,
        Hotel: item.hotelName,
        Customer: item.fullName,
        Phone: item.phoneNumber,
        Rooms: item.rooms?.length || 0,
        Guests: item.guests?.length || 0,
        TotalAmount: item.totalAmount,
        PaidAmount: item.paidAmount,
        ServiceCharge: item.taxAmount,
        Status: item.paymentStatus,
        Date: item.createdAt?.slice(0, 10),
      })),
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
    XLSX.writeFile(workbook, "hotel-booking-report.xlsx");
  };

  const handleRestFilter = () => {
    setStartDate("");
    setEndDate("");
    setSelectedHotel("");
    setSelectedCustomer("");
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div>
          <label className="text-xs">Hotels</label>
          <select
            value={SelectedHotel}
            className="w-full px-4 py-2 rounded-lg border focus:outline-none text-sm"
            onChange={handleHotelChange}
          >
            <option value="">Select Hotel</option>
            {Hotels.map((item) => (
              <option key={item.id} value={item.id}>
                {item.hotelName}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs">Customers</label>
          <select
            value={SelectedCustomer}
            className="w-full px-4 py-2 rounded-lg border focus:outline-none text-sm"
            onChange={handleCustomerChange}
          >
            <option value="">Select Customer</option>
            {Customers.map((item) => (
              <option key={item.id} value={item.id}>
                {item.fullName}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3 w-full">
          <div>
            <label className="text-xs">Start Date</label>
            <input
              type="date"
              className="w-full border px-4 py-2 rounded-lg focus:outline-none"
              value={StartDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs">End Date</label>
            <input
              type="date"
              className="w-full border px-4 py-2 rounded-lg focus:outline-none"
              value={EndDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="p-4 w-full overflow-x-auto space-y-3">
        <div className="flex justify-between items-center">
          <p className="text-lg font-semibold uppercase">
            Hotel Booking Report
          </p>
          <div className="flex justify-start items-center gap-2">
            <SendButton text={"Excel"} onClick={exportToExcel} />
            <CancelButton text={"Reset Filter"} onClick={handleRestFilter} />
          </div>
        </div>

        <table className="min-w-full border border-gray-200 bg-white rounded-lg overflow-hidden">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">
                ID
              </th>

              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">
                Trx ID
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">
                Hotel Name
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">
                Customer Name
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">
                Total Rooms
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">
                Total Guest
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">
                Total Amount
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">
                Service Charge
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">
                Paid Amount
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">
                Payment Status
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">
                Payment Method
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">
                Booking Date
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200">
            {Data.length > 0 ? (
              <>
                {Data.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {index + 1}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {item.invoiceId}
                    </td>

                    <td className="px-4 py-3 text-sm text-gray-700">
                      {item.hotelName}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {item.fullName} <br />
                      <span className="text-xs">{item.phoneNumber}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {item.rooms?.length || 0} Room
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {item.guests?.length || 0} person
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {Number(item.totalAmount || 0).toFixed(0)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {Number(item.taxAmount || 0).toFixed(0)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {Number(item.paidAmount || 0).toFixed(0)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`px-2 py-1 ${
                          item.paymentStatus === "Paid"
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        } rounded text-xs`}
                      >
                        {item.paymentStatus === "Paid" ? "Paid" : "Not Paid"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {item.paymentSource || ""}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {item.createdAt?.slice(0, 10)}
                    </td>
                  </tr>
                ))}

                <tr className="bg-gray-50">
                  <td
                    colSpan={7}
                    className="px-4 py-3 text-right text-sm font-bold"
                  >
                    Total:
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold">
                    {totalSummary.totalAmount.toFixed(0)}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold">
                    {totalSummary.taxAmount.toFixed(0)}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold">
                    {totalSummary.paidAmount.toFixed(0)}
                  </td>
                  <td></td>
                  <td></td>
                </tr>
              </>
            ) : (
              <tr className="hover:bg-gray-50">
                <td
                  colSpan={12}
                  className="px-4 py-3 text-center text-sm text-gray-700"
                >
                  {IsLoading ? "Loading..." : "No Data to show"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HotelBookingReport;
